import React, { useState, useEffect } from "react";
import { BarChart3, Download, FolderOpen, CheckCircle, Clock, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsMap, setStatsMap] = useState({});

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/projects`);
        setProjects(data.filter(p => !p.is_archived));
        // Fetch stats for each project
        // Stats for every active project, fetched in small batches so a big
        // workspace doesn't fire hundreds of requests at once.
        const live = data.filter(p => !p.is_archived);
        setLoading(false);                       // table renders now; stats stream in per batch
        for (let i = 0; i < live.length; i += 8) {
          const chunk = await Promise.all(live.slice(i, i + 8).map(p =>
            axios.get(`${API}/projects/${p.id}/stats`).then(r => ({ id: p.id, ...r.data })).catch(() => null)
          ));
          setStatsMap(prev => { const next = { ...prev }; chunk.filter(Boolean).forEach(s => { next[s.id] = s; }); return next; });
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetch();
  }, []);

  const totalProjects = projects.length;
  const totalMWp = projects.reduce((a, p) => a + (p.capacity_kwp || 0), 0) / 1000;
  const totalValue = projects.reduce((a, p) => a + (p.contract_value || 0), 0);
  const activeProjects = projects.filter(p => p.status === "active").length;
  const completedProjects = projects.filter(p => p.status === "completed").length;
  const delayedProjects = projects.filter(p => p.rag_status === "red").length;

  const formatValue = (n) => {
    if (!n) return "₹0";
    if (n >= 10000000) return `₹${(n/10000000).toFixed(1)} Cr`;
    if (n >= 100000) return `₹${(n/100000).toFixed(1)} L`;
    return `₹${n.toLocaleString("en-IN")}`;
  };

  const chartData = projects.slice(0, 8).map(p => ({
    name: p.name.length > 12 ? p.name.slice(0, 12) + "..." : p.name,
    completion: p.completion_percentage || 0,
    color: p.rag_status === "green" ? "#059669" : p.rag_status === "amber" ? "#D97706" : "#DC2626"
  }));

  const exportReport = () => {
    const now = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    const rows = projects.map(p => {
      const s = statsMap[p.id];
      const ragLabel = { green: "On Track", amber: "At Risk", red: "Delayed" }[p.rag_status] || "Unknown";
      const ragColor = p.rag_status === "red" ? "#dc2626" : p.rag_status === "amber" ? "#d97706" : "#059669";
      return `<tr>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;font-weight:500">${p.name}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;color:#475569">${p.client_name}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center">${p.capacity_kwp} kWp</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center">Phase ${p.current_phase}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center;font-weight:600">${p.completion_percentage || 0}%</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center">${s?.by_status?.completed || 0}/${s?.total_tasks || 0}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:center;color:${ragColor};font-weight:600">${ragLabel}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #f1f5f9;text-align:right;font-weight:600">${formatValue(p.contract_value)}</td>
      </tr>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head>
      <meta charset="UTF-8">
      <title>SunBiz — Project Status Report</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; font-size:12px; color:#1e293b; background:#fff; padding:32px; }
        h1 { font-size:22px; font-weight:700; color:#0A1128; margin-bottom:4px; }
        .meta { color:#64748B; font-size:12px; margin-bottom:24px; }
        .summary { display:flex; gap:16px; margin-bottom:24px; flex-wrap:wrap; }
        .card { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:12px 20px; min-width:120px; }
        .card .val { font-size:20px; font-weight:700; color:#0A1128; }
        .card .lbl { font-size:10px; text-transform:uppercase; color:#64748B; letter-spacing:0.05em; margin-top:2px; }
        table { width:100%; border-collapse:collapse; }
        th { background:#0A1128; color:white; padding:8px 12px; text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:0.05em; }
        tr:nth-child(even) { background:#f8fafc; }
        @media print { body { padding:16px; } .no-print { display:none; } @page { margin:15mm; } }
      </style>
    </head><body>
      <div class="no-print" style="text-align:right;margin-bottom:20px">
        <button onclick="window.print()" style="background:#FFB703;border:none;padding:8px 20px;font-size:13px;font-weight:600;cursor:pointer;border-radius:4px">Print / Save as PDF</button>
      </div>
      <h1>SunBiz — Project Status Report</h1>
      <p class="meta">Generated on ${now} &nbsp;|&nbsp; ${totalProjects} Projects &nbsp;|&nbsp; ${totalMWp.toFixed(2)} MWp Portfolio</p>
      <div class="summary">
        <div class="card"><div class="val">${totalProjects}</div><div class="lbl">Total Projects</div></div>
        <div class="card"><div class="val" style="color:#059669">${activeProjects}</div><div class="lbl">Active</div></div>
        <div class="card"><div class="val" style="color:#7c3aed">${completedProjects}</div><div class="lbl">Completed</div></div>
        <div class="card"><div class="val" style="color:#dc2626">${delayedProjects}</div><div class="lbl">Delayed</div></div>
        <div class="card"><div class="val">${totalMWp.toFixed(2)} MWp</div><div class="lbl">Total Capacity</div></div>
        <div class="card"><div class="val">${formatValue(totalValue)}</div><div class="lbl">Portfolio Value</div></div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Project</th><th>Client</th><th style="text-align:center">Capacity</th>
            <th style="text-align:center">Phase</th><th style="text-align:center">Progress</th>
            <th style="text-align:center">Tasks</th><th style="text-align:center">Status</th>
            <th style="text-align:right">Value</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      toast.error("Please allow popups for this site to open print preview");
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  if (loading) return <div className="p-8 text-[#64748B]">Loading reports...</div>;

  return (
    <div className="p-6 animate-fade-up">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-2xl text-[#0A1128]">Reports & Analytics</h1>
        <button
          onClick={exportReport}
          className="flex items-center gap-2 border border-[#E2E8F0] text-[#64748B] px-4 py-2 rounded text-sm hover:bg-slate-50 transition-colors"
          data-testid="export-report-btn"
        >
          <Download size={14} />Export Report
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {[
          { label: "Total Projects", value: totalProjects, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Active", value: activeProjects, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Completed", value: completedProjects, color: "text-purple-600", bg: "bg-purple-50" },
          { label: "Delayed", value: delayedProjects, color: "text-red-600", bg: "bg-red-50" },
          { label: "Total MWp", value: `${totalMWp.toFixed(2)} MWp`, color: "text-amber-600", bg: "bg-amber-50" },
          { label: "Portfolio Value", value: formatValue(totalValue), color: "text-[#0A1128]", bg: "bg-slate-50" },
        ].map(card => (
          <div key={card.label} className="bg-white border border-[#E2E8F0] rounded p-4">
            <div className="text-[9px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">{card.label}</div>
            <div className={`font-heading font-bold text-lg ${card.color}`}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="bg-white border border-[#E2E8F0] rounded p-6 mb-8">
        <h3 className="font-heading font-semibold text-sm text-[#0A1128] mb-4">Project Completion Rate</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} barSize={28} margin={{ bottom: 20 }}>
            <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#64748B" }} axisLine={false} tickLine={false} angle={-30} textAnchor="end" />
            <YAxis tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} domain={[0, 100]} tickFormatter={v => `${v}%`} />
            <Tooltip cursor={{ fill: "#F8FAFC" }} formatter={(v) => [`${v}%`, "Completion"]} contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }} />
            <Bar dataKey="completion" radius={[3, 3, 0, 0]}>
              {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Projects Table */}
      <div className="bg-white border border-[#E2E8F0] rounded overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E2E8F0]">
          <h3 className="font-heading font-semibold text-sm text-[#0A1128]">Project Status Report</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                {["Project", "Client", "Capacity", "Phase", "Progress", "Tasks Done", "Overdue", "RAG", "Value"].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projects.map(p => {
                const s = statsMap[p.id];
                return (
                  <tr key={p.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]">
                    <td className="px-4 py-3 font-medium text-[#0A1128] text-xs max-w-36 truncate">{p.name}</td>
                    <td className="px-4 py-3 text-xs text-[#64748B]">{p.client_name}</td>
                    <td className="px-4 py-3 text-xs tabular-nums">{p.capacity_kwp} kWp</td>
                    <td className="px-4 py-3 text-xs text-[#64748B]">Phase {p.current_phase}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
                          <div className="h-full bg-[#FFB703] rounded-full" style={{ width: `${p.completion_percentage || 0}%` }} />
                        </div>
                        <span className="text-xs tabular-nums">{p.completion_percentage || 0}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs tabular-nums">{s?.by_status?.completed || 0}/{s?.total_tasks || 0}</td>
                    <td className="px-4 py-3 text-xs tabular-nums text-red-600">{s?.overdue_tasks?.length || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${p.rag_status === "green" ? "bg-emerald-100 text-emerald-700" : p.rag_status === "amber" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {({ green: "On Track", amber: "At Risk", red: "Delayed" })[p.rag_status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs tabular-nums font-medium">{formatValue(p.contract_value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
